import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Download, Volume2, VolumeX } from 'lucide-react';
import { HeroContrast } from './HeroContrast';
import { Manifesto } from './Manifesto';
import { TimeLoss } from './TimeLoss';
import { Philosophy } from './Philosophy';
import { Roles } from './Roles';
import { Solutions } from './Solutions';
import { Closing } from './Closing';
import { BuildSolution } from './BuildSolution';
import { PricingSlide } from '../PresentationPage';
import './accueil.css';

const SoundToggle = () => {
  const audio = useRef(null);
  const [on, setOn] = useState(false);
  useEffect(() => () => audio.current?.pause(), []);
  const toggle = () => {
    if (!audio.current) {
      audio.current = new Audio('/accueil/musique.mp3');
      audio.current.loop = true;
    }
    const a = audio.current;
    if (on) { a.pause(); setOn(false); return; }
    a.volume = 0;
    a.play().then(() => {
      setOn(true);
      const fade = setInterval(() => { a.volume = Math.min(0.45, a.volume + 0.03); if (a.volume >= 0.45) clearInterval(fade); }, 120);
    }).catch(() => {});
  };
  return (
    <button onClick={toggle} data-testid="sound-toggle-button" aria-label={on ? 'Couper la musique' : 'Écouter la musique'}
      className="flex items-center gap-2 h-10 px-3 rounded-full bg-white/10 backdrop-blur-xl border border-white/15 text-white/80 hover:text-white hover:bg-white/15 transition-colors text-xs">
      {on ? <Volume2 size={16} /> : <VolumeX size={16} />}
      <span className="hidden md:inline">{on ? 'Musique' : 'Respirez — activer la musique'}</span>
    </button>
  );
};

const Header = () => {
  const navigate = useNavigate();
  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-[#0B0F19]/60 backdrop-blur-xl border-b border-white/5" data-testid="accueil-header">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
        <a href="/presentation" className="flex items-center gap-2.5" data-testid="accueil-logo">
          <img src="/icons/icon-192.png" alt="" className="w-8 h-8 rounded-lg" />
          <span className="hidden sm:inline text-white font-bold tracking-tight">ProPilote</span>
        </a>
        <div className="flex items-center gap-2 sm:gap-3">
          <SoundToggle />
          <button onClick={() => navigate('/register')} data-testid="header-driver-app-btn"
            className="flex items-center gap-2 h-10 px-3 sm:px-4 rounded-full border border-white/15 text-white text-sm font-medium hover:border-white/40 transition-colors" aria-label="App chauffeur gratuite">
            <Download size={15} /> <span className="hidden sm:inline">App chauffeur gratuite</span>
          </button>
          <button onClick={() => navigate('/admin/register')} data-testid="header-trial-btn"
            className="flex items-center gap-2 h-10 px-4 sm:px-5 rounded-full bg-amber-500 hover:bg-amber-400 text-black text-sm font-bold whitespace-nowrap transition-colors">
            Essai gratuit <ArrowRight size={15} />
          </button>
        </div>
      </div>
    </header>
  );
};

export default function AccueilPage() {
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (id) setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }), 400);
  }, []);
  return (
    <div className="pp-accueil bg-[#0B0F19] text-slate-300 min-h-screen" data-testid="accueil-page">
      <Header />
      <HeroContrast />
      <Manifesto />
      <TimeLoss />
      <Philosophy />
      <Roles />
      <Solutions />
      <section className="bg-[#080B13] py-28 md:py-36" id="tarifs" data-testid="pricing-section">
        <PricingSlide isActive />
      </section>
      <BuildSolution />
      <Closing />
      <footer className="bg-[#0B0F19] border-t border-white/5 py-8 text-center text-xs text-slate-500" data-testid="accueil-footer">
        © {new Date().getFullYear()} ProPilote · <a href="/privacy" className="hover:text-slate-300">Confidentialité</a> · <a href="/terms" className="hover:text-slate-300">Conditions</a>
      </footer>
    </div>
  );
}
